import { useCallback, useMemo, useState } from 'react';
import {
  HsmTemplate,
  HsmTemplatesResponse,
  HsmTestMessageRequest
} from '../types';
import * as hsmService from '../services/hsm.service';
import { withAuthRetry } from '../utils/withAuthRetry';
import {
  saveTemplateVariableMapping as saveTemplateVariableMappingService,
  getTemplateVariableMapping as getTemplateVariableMappingService,
  getAllTemplateVariableMappings as getAllTemplateVariableMappingsService,
  deleteTemplate as deleteTemplateService
} from '../services/hsm-templates.service';
import type {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateVariable
} from '../services/hsm.service';

export interface UseHsmTemplatesProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
}

export interface UseHsmTemplatesReturn {
  // Templates state
  templates: HsmTemplate[];
  templatesLoading: boolean;
  templatesResponse: HsmTemplatesResponse | null;
  approvedTemplates: HsmTemplate[];
  templateCreating: boolean;
  templateUpdating: boolean;
  selectedTemplate: HsmTemplate | null;
  editTemplateDialogOpen: boolean;
  setEditTemplateDialogOpen: (open: boolean) => void;

  // Template variables state
  templateVariables: TemplateVariable[];
  templateVariablesLoading: boolean;

  // Test message state
  testDialogOpen: boolean;
  setTestDialogOpen: (open: boolean) => void;
  testPhoneNumber: string;
  setTestPhoneNumber: (value: string) => void;
  testTemplateName: string;
  setTestTemplateName: (value: string) => void;
  testLanguageCode: string;
  setTestLanguageCode: (value: string) => void;
  testTemplateParameters: string;
  setTestTemplateParameters: (value: string) => void;
  testSending: boolean;

  // Actions
  fetchTemplates: () => Promise<{ success: boolean; error?: string }>;
  createTemplate: (data: CreateTemplateRequest) => Promise<{ success: boolean; error?: string }>;
  updateTemplate: (templateId: string, data: UpdateTemplateRequest) => Promise<{ success: boolean; error?: string }>;
  openEditTemplateDialog: (template: HsmTemplate) => void;
  closeEditTemplateDialog: () => void;

  // Template variables actions
  fetchTemplateVariables: () => Promise<{ success: boolean; error?: string }>;
  validateTemplateVariables: (templateBody?: string, variableMapping?: string[]) => Promise<{
    success: boolean;
    valid?: boolean;
    errors?: string[];
    detectedVariables?: string[];
    error?: string;
  }>;
  parseTemplateVariables: (templateBody: string) => Promise<{
    success: boolean;
    variables?: string[];
    placeholders?: string[];
    error?: string;
  }>;

  // Test message actions
  sendTestMessage: (data: HsmTestMessageRequest) => Promise<{ success: boolean; error?: string }>;
  openTestDialog: () => void;
  resetTestForm: () => void;

  // Template variable mappings actions
  saveTemplateVariableMapping: (templateName: string, variableMapping: string[]) => Promise<{ success: boolean; error?: string }>;
  getTemplateVariableMapping: (templateName: string) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
  getAllTemplateVariableMappings: () => Promise<{ success: boolean; mappings?: Map<string, string[]>; error?: string }>;

  // Delete template action
  deleteTemplate: (templateName: string) => Promise<{ success: boolean; error?: string }>;
  templateDeleting: boolean;
}

export function useHsmTemplates({
  accessToken,
  refreshToken,
  attemptRefresh,
  canManageHsm
}: UseHsmTemplatesProps): UseHsmTemplatesReturn {
  // Templates state
  const [templates, setTemplates] = useState<HsmTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesResponse, setTemplatesResponse] = useState<HsmTemplatesResponse | null>(null);
  const [templateCreating, setTemplateCreating] = useState(false);
  const [templateUpdating, setTemplateUpdating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<HsmTemplate | null>(null);
  const [editTemplateDialogOpen, setEditTemplateDialogOpen] = useState(false);

  // Template variables state
  const [templateVariables, setTemplateVariables] = useState<TemplateVariable[]>([]);
  const [templateVariablesLoading, setTemplateVariablesLoading] = useState(false);

  // Delete template state
  const [templateDeleting, setTemplateDeleting] = useState(false);

  // Test message state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testTemplateName, setTestTemplateName] = useState('');
  const [testLanguageCode, setTestLanguageCode] = useState('');
  const [testTemplateParameters, setTestTemplateParameters] = useState('');
  const [testSending, setTestSending] = useState(false);

  // Computed: Get only approved templates for dropdowns
  const approvedTemplates = useMemo(() => {
    return templates.filter((t) => t.status === 'APPROVED');
  }, [templates]);

  // Templates API
  const fetchTemplates = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageHsm) return { success: false, error: 'Nao autorizado' };

    setTemplatesLoading(true);

    try {
      const data = await withAuthRetry(
        () => hsmService.fetchTemplates(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setTemplatesResponse(data);
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar templates'
      };
    } finally {
      setTemplatesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageHsm]);

  const createTemplate = useCallback(
    async (data: CreateTemplateRequest): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setTemplateCreating(true);

      try {
        await withAuthRetry(
          () => hsmService.createTemplate(accessToken!, data),
          refreshToken,
          attemptRefresh
        );
        await fetchTemplates();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao criar template'
        };
      } finally {
        setTemplateCreating(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchTemplates]
  );

  const updateTemplate = useCallback(
    async (templateId: string, data: UpdateTemplateRequest): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setTemplateUpdating(true);

      try {
        await withAuthRetry(
          () => hsmService.updateTemplate(accessToken!, templateId, data),
          refreshToken,
          attemptRefresh
        );
        await fetchTemplates();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao atualizar template'
        };
      } finally {
        setTemplateUpdating(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchTemplates]
  );

  const openEditTemplateDialog = useCallback((template: HsmTemplate) => {
    setSelectedTemplate(template);
    setEditTemplateDialogOpen(true);
  }, []);

  const closeEditTemplateDialog = useCallback(() => {
    setEditTemplateDialogOpen(false);
    setSelectedTemplate(null);
  }, []);

  // Template variables API
  const fetchTemplateVariables = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageHsm) return { success: false, error: 'Nao autorizado' };

    setTemplateVariablesLoading(true);

    try {
      const data = await withAuthRetry(
        () => hsmService.fetchTemplateVariables(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setTemplateVariables(Array.isArray(data.variables) ? data.variables : []);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar variaveis de template'
      };
    } finally {
      setTemplateVariablesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageHsm]);

  const validateTemplateVariables = useCallback(
    async (templateBody?: string, variableMapping?: string[]): Promise<{ success: boolean; valid?: boolean; errors?: string[]; detectedVariables?: string[]; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        const result = await withAuthRetry(
          () => hsmService.validateTemplateVariables(accessToken!, { templateBody, variableMapping }),
          refreshToken,
          attemptRefresh
        );
        return { success: true, valid: result.valid, errors: result.errors, detectedVariables: result.detectedVariables };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao validar variaveis'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const parseTemplateVariables = useCallback(
    async (templateBody: string): Promise<{ success: boolean; variables?: string[]; placeholders?: string[]; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        const result = await withAuthRetry(
          () => hsmService.parseTemplateVariables(accessToken!, { templateBody }),
          refreshToken,
          attemptRefresh
        );
        return { success: true, variables: result.variables, placeholders: result.placeholders };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao analisar template'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // Test message
  const sendTestMessage = useCallback(
    async (data: HsmTestMessageRequest): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setTestSending(true);

      try {
        await withAuthRetry(
          () => hsmService.sendTestMessage(data, accessToken!),
          refreshToken,
          attemptRefresh
        );
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao enviar mensagem de teste'
        };
      } finally {
        setTestSending(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const resetTestForm = useCallback(() => {
    setTestPhoneNumber('');
    setTestTemplateName('');
    setTestLanguageCode('');
    setTestTemplateParameters('');
  }, []);

  const openTestDialog = useCallback(() => {
    resetTestForm();
    setTestDialogOpen(true);
  }, [resetTestForm]);

  // Template variable mappings
  const saveTemplateVariableMapping = useCallback(
    async (templateName: string, variableMapping: string[]): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        await withAuthRetry(
          () => saveTemplateVariableMappingService(accessToken!, templateName, variableMapping),
          refreshToken,
          attemptRefresh
        );
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao salvar mapeamento de variaveis'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const getTemplateVariableMapping = useCallback(
    async (templateName: string): Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        const variableMapping = await withAuthRetry(
          () => getTemplateVariableMappingService(accessToken!, templateName),
          refreshToken,
          attemptRefresh
        );
        return { success: true, variableMapping };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao obter mapeamento de variaveis'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const getAllTemplateVariableMappings = useCallback(
    async (): Promise<{ success: boolean; mappings?: Map<string, string[]>; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        const mappings = await withAuthRetry(
          () => getAllTemplateVariableMappingsService(accessToken!),
          refreshToken,
          attemptRefresh
        );
        return { success: true, mappings };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao obter mapeamentos de variaveis'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // Delete template
  const deleteTemplate = useCallback(
    async (templateName: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setTemplateDeleting(true);

      try {
        await withAuthRetry(
          () => deleteTemplateService(accessToken!, templateName),
          refreshToken,
          attemptRefresh
        );
        await fetchTemplates();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao excluir template'
        };
      } finally {
        setTemplateDeleting(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchTemplates]
  );

  return {
    // Templates state
    templates,
    templatesLoading,
    templatesResponse,
    approvedTemplates,
    templateCreating,
    templateUpdating,
    selectedTemplate,
    editTemplateDialogOpen,
    setEditTemplateDialogOpen,

    // Template variables state
    templateVariables,
    templateVariablesLoading,

    // Test message state
    testDialogOpen,
    setTestDialogOpen,
    testPhoneNumber,
    setTestPhoneNumber,
    testTemplateName,
    setTestTemplateName,
    testLanguageCode,
    setTestLanguageCode,
    testTemplateParameters,
    setTestTemplateParameters,
    testSending,

    // Actions
    fetchTemplates,
    createTemplate,
    updateTemplate,
    openEditTemplateDialog,
    closeEditTemplateDialog,

    // Template variables actions
    fetchTemplateVariables,
    validateTemplateVariables,
    parseTemplateVariables,

    // Test message actions
    sendTestMessage,
    openTestDialog,
    resetTestForm,

    // Template variable mappings actions
    saveTemplateVariableMapping,
    getTemplateVariableMapping,
    getAllTemplateVariableMappings,

    // Delete template action
    deleteTemplate,
    templateDeleting
  };
}
